import { createContext, useContext, useMemo } from "react";

import { Stack, Switch } from "@mantine/core";
import { readLocalStorageValue, useLocalStorage } from "@mantine/hooks";
import { modals } from "@mantine/modals";

function createFeatureToggleHooks({ key, defaultValue }: { key: string; defaultValue: boolean }) {
  function useFeatureToggle() {
    const [enabled, setEnabled] = useLocalStorage({
      key,
      defaultValue,
    });
    const result = useMemo(() => [enabled, setEnabled] as const, [enabled, setEnabled]);
    return result;
  }
  function getFeatureEnabled() {
    return readLocalStorageValue({
      key,
      defaultValue,
    });
  }
  return [useFeatureToggle, getFeatureEnabled] as const;
}

const ENABLE_FRONTEND_PERFORMANCE_LOGGING_KEY = "enable-frontend-performance-logging";
const [useEnableFrontendPerformanceLogging, getEnableFrontendPerformanceLogging] =
  createFeatureToggleHooks({
    key: ENABLE_FRONTEND_PERFORMANCE_LOGGING_KEY,
    defaultValue: false,
  });
export { useEnableFrontendPerformanceLogging, getEnableFrontendPerformanceLogging };

const ENABLE_IN_CYCLE_PAY_FREQUENCY_CHANGE_HOOK_KEY = "enable-in-cycle-pay-frequency-change-hook";
const [useEnableInCyclePayFrequencyChangeHook, getEnableInCyclePayFrequencyChangeHook] =
  createFeatureToggleHooks({
    key: ENABLE_IN_CYCLE_PAY_FREQUENCY_CHANGE_HOOK_KEY,
    defaultValue: false,
  });
export { useEnableInCyclePayFrequencyChangeHook, getEnableInCyclePayFrequencyChangeHook };

const ENABLE_CYCLE_HEALTH_ALERT_KEY = "enable-cycle-health-alert";
const [useEnableCycleHealthAlert, getEnableCycleHealthAlert] = createFeatureToggleHooks({
  key: ENABLE_CYCLE_HEALTH_ALERT_KEY,
  defaultValue: false,
});
export { useEnableCycleHealthAlert, getEnableCycleHealthAlert };

const ENABLE_INTEGRATIONS_UI_V2_KEY = "enable-integrations-ui-v2";
const [useEnableIntegrationsUiV2, getEnableIntegrationsUiV2] = createFeatureToggleHooks({
  key: ENABLE_INTEGRATIONS_UI_V2_KEY,
  defaultValue: false,
});
export { useEnableIntegrationsUiV2, getEnableIntegrationsUiV2 };

type FeatureToggleContextValue = {
  frontendPerfLoggingEnabled: boolean;
  inCyclePayFrequencyChangeEnabled: boolean;
  cycleHealthAlertEnabled: boolean;
  integrationsUiV2Enabled: boolean;
};

const FeatureToggleContext = createContext<FeatureToggleContextValue>(
  {} as FeatureToggleContextValue,
);

export function FeatureToggleProvider({ children }: { children: React.ReactNode }) {
  const [frontendPerfLoggingEnabled] = useEnableFrontendPerformanceLogging();
  const [inCyclePayFrequencyChangeEnabled] = useEnableInCyclePayFrequencyChangeHook();
  const [cycleHealthAlertEnabled] = useEnableCycleHealthAlert();
  const [integrationsUiV2Enabled] = useEnableIntegrationsUiV2();

  const value = useMemo(
    () => ({
      frontendPerfLoggingEnabled,
      inCyclePayFrequencyChangeEnabled,
      cycleHealthAlertEnabled,
      integrationsUiV2Enabled,
    }),
    [
      frontendPerfLoggingEnabled,
      inCyclePayFrequencyChangeEnabled,
      cycleHealthAlertEnabled,
      integrationsUiV2Enabled,
    ],
  );
  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
}

export function useFeatureToggles() {
  return useContext(FeatureToggleContext);
}

export function FeatureToggles() {
  const [frontendPerfLoggingEnabled, setFrontendPerfLoggingEnabled] =
    useEnableFrontendPerformanceLogging();

  const [inCyclePayFrequencyChangeEnabled, setInCyclePayFrequencyChangeEnabled] =
    useEnableInCyclePayFrequencyChangeHook();

  const [cycleHealthAlertEnabled, setCycleHealthAlertEnabled] = useEnableCycleHealthAlert();

  const [integrationsUiV2Enabled, setIntegrationsUiV2Enabled] = useEnableIntegrationsUiV2();

  return (
    <Stack>
      <Switch
        label="Enable frontend performance logging"
        checked={frontendPerfLoggingEnabled}
        onChange={(event) => setFrontendPerfLoggingEnabled(event.target.checked)}
      />
      <Switch
        label="Enable in-cycle pay frequency change"
        checked={inCyclePayFrequencyChangeEnabled}
        onChange={(event) => setInCyclePayFrequencyChangeEnabled(event.target.checked)}
      />
      <Switch
        label="Enable cycle health alert"
        checked={cycleHealthAlertEnabled}
        onChange={(event) => setCycleHealthAlertEnabled(event.target.checked)}
      />
      <Switch
        label="Enable integrations UI v2"
        checked={integrationsUiV2Enabled}
        onChange={(event) => setIntegrationsUiV2Enabled(event.target.checked)}
      />
    </Stack>
  );
}

export function openFeatureTogglesModal() {
  modals.open({
    title: "Feature toggles",
    children: <FeatureToggles />,
  });
}
